import React, { useState } from "react";
import Box from "@mui/material/Box";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SuccessModal from "../../utils/SuccessModal";
import ErrorModal from "../../utils/ErrorModal";

const Rfqs = ({ rfq, isLoading, activeTab, getRFQs }) => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState();
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState();

  const handleStatusUpdate = async (id, status) => {
    setLoading(true);
    setErrorMsg("");
    setOpenError(false);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-rfq?vendorId=${vendorId}&rfqId=${id}&status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setErrorMsg("");
      setOpenError(false);
      setOpen(true);
      if (status === "INTERESTED") {
        setMsg("RFQ marked as Interested");
      } else {
        setMsg("RFQ marked as Not Interested");
      }
      await getRFQs();
    } catch (error) {
      setErrorMsg(error?.response?.data?.Exception || "Something went wrong");
      setOpenError(true);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "rfqId",
      headerName: "RFQ ID",
      flex: 1,
      renderCell: (params) => (
        <span
          className="table-title"
          onClick={() => navigate(`/vendor/dashboard/${params.row.rfqId}`)}
        >
          {params.row.rfqId}
        </span>
      ),
    },
    {
      field: "orgName",
      headerName: "Organization",
      flex: 1,
    },
    {
      field: "creationDate",
      headerName: "Creation Date",
      flex: 1,
      renderCell: (params) => formatDateTime(params.row.creationDate),
    },
    {
      field: "submissionDate",
      headerName: "Submission Date",
      flex: 1,
      renderCell: (params) => {
        const { rebidEndDate, submissionDate } = params.row;
        return formatDateTime(rebidEndDate || submissionDate);
      },
    },
    {
      field: "vrStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const statusColors = {
          INVITED: "#35D85B",
          INTERESTED: "#1DCCE8",
          NOT_INTERESTED: "#1DCCE8",
          EAUCTIONED: "#1DCCE8",
          BID_OPEN: "#35D85B",
          BID_CLOSED: "rgb(190, 179, 179)",
          BID_SUBMITTED: "#35D85B",
          REBID: "#35D85B",
          DISQUALIFIED: "#FA6459",
          AWARDED: "#0381F3",
        };
        const statusLabels = {
          INVITED: "INVITED",
          INTERESTED: "INTERESTED",
          NOT_INTERESTED: "NOT INTERESTED",
          EAUCTIONED: "EAUCTIONED",
          BID_OPEN: "RFQ OPEN",
          BID_CLOSED: "RFQ CLOSED",
          BID_SUBMITTED: "QUOTE SUBMITTED",
          REBID: "REVISE RFQ",
          DISQUALIFIED: "DISQUALIFIED",
          AWARDED: "AWARDED",
        };
        return (
          <span
            style={{
              background: statusColors[params.row.vrStatus],
              borderRadius: "15px",
              padding: "5px 15px",
              fontWeight: 600,
            }}
          >
            {statusLabels[params.row.vrStatus]}
          </span>
        );
      },
    },
    activeTab !== "POSTBIDDING" && {
      field: "action",
      headerName: "Action",
      flex: 2,
      renderCell: (params) =>
        params.row.vrStatus === "INVITED" ? (
          <Box sx={{ display: "inline-flex", gap: 2 }}>
            <Button
              className="btn-contained"
              sx={{
                margin: "0 !important",
                fontSize: "12px",
                width: "130px !important",
              }}
              onClick={() => handleStatusUpdate(params.row.rfqId, "INTERESTED")}
            >
              Interested
            </Button>
            <Button
              className="btn-outlined btn-reject"
              sx={{
                margin: "0 !important",
                fontSize: "12px",
                width: "130px !important",
              }}
              onClick={() =>
                handleStatusUpdate(params.row.rfqId, "NOT_INTERESTED")
              }
            >
              Not Interested
            </Button>
          </Box>
        ) : params.row.vrStatus === "BID_OPEN" ? (
          <>
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() => handleNavigateToBid(params.row.rfqId)}
            >
              Submit Quote
            </Button>
          </>
        ) : params.row.vrStatus === "REBID" &&
          new Date(params.row.rebidStartDate) <= new Date() &&
          new Date(params.row.rebidEndDate) >= new Date() &&
          params.row.rfqStatus !== "BIDS_CLOSED" ? (
          <>
            <Button
              className="btn-contained"
              sx={{ margin: "0 !important", fontSize: "12px" }}
              onClick={() => {
                handleNavigateToBid(params.row.rfqId);
                localStorage.setItem("isRebid", true);
              }}
            >
              Submit Revised Quote
            </Button>
          </>
        ) : null,
    },
  ];

  const handleNavigateToBid = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid/check?vendorId=${vendorId}&rfqId=${id}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      if (res.data === true) {
        navigate(`/vendor/dashboard/submit-bid/draft/${id}`);
      } else {
        navigate(`/vendor/dashboard/submit-bid/${id}`);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    const cleanTime = time?.split(".")[0];
    return `${day}/${month}/${year} ${cleanTime}`;
  };

  return (
    <div style={{ position: "relative" }}>
      {loading && <div className="loading-div"></div>}
      <Box sx={{ background: "#fff" }}>
        <DataGrid
          rows={rfq}
          columns={columns}
          loading={isLoading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          disableColumnResize
          getRowId={(row) => row.vrId}
          slots={{ toolbar: CustomToolbar }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          sx={{
            position: "relative",
            border: "none",
            fontFamily: "Inter !important",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "700 !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              background: "none !important",
              color: "black",
              fontSize: "14px",
              borderRadius: "10px",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              border: "none",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
              color: "#000 !important",
              border: "1px solid #B0B0B0",
              borderRadius: "12px",
              padding: 1,
            },
            "& .MuiInputBase-root-MuiInput-root::after": {
              borderBottom: "none !important",
            },
            "& .MuiInputBase-root-MuiInput-root": {
              borderBottom: "none !important",
            },
            [`& .${gridClasses.row}.even`]: {
              backgroundColor: "#EAF4FF",
              // '&:hover': {
              //   backgroundColor: "#f6f6f6",
              // },
            },
            "& .MuiInput-underline:before": {
              borderBottom: "none !important",
            },
            "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
              borderBottom: "none !important",
            },
          }}
        />
      </Box>

      <SuccessModal open={open} handleClose={() => setOpen(false)} msg={msg} />
      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default Rfqs;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end" }}>
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #B0B0B0",
          borderRadius: "12px",
          padding: "4px 12px",
          "&:hover": {
            backgroundColor: "#D8E9F0",
          },
          "& .MuiInputBase-root": {
            borderRadius: "12px",
          },
          width: 300,
        }}
      />
      {/* <GridToolbarFilterButton /> */}
    </GridToolbarContainer>
  );
}